import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { signUp } from '@/lib/queries/auth';

import {
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

export const SignUpForm = (): JSX.Element => {
  const { formatMessage } = useIntl();
  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues,
  });
  const { toast } = useToast();

  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => signUp(email, password),
    onError: () => {
      toast({
        title: 'auth.signUp.form.submit.error',
        variant: 'destructive',
      });
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  if (isSuccess) {
    return <Navigate to="/not-verified" />;
  }

  return (
    <Form {...form}>
      <form
        className="space-y-4 md:space-y-6"
        onSubmit={form.handleSubmit(async (data: FormValues) => {
          try {
            await mutateAsync(data);
          } catch (err) {
            console.error(err);
          }
        })}
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                <FormattedMessage id="auth.signUp.form.email.label" />
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  type="text"
                  placeholder={formatMessage({
                    id: 'auth.signUp.form.email.placeholder',
                  })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                <FormattedMessage id="auth.signUp.form.password.label" />
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    {...field}
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={formatMessage({
                      id: 'auth.signUp.form.password.placeholder',
                    })}
                  />
                  <Button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
          disabled={isPending}
        >
          {isPending ? (
            <FormattedMessage id="auth.signUp.form.submit.pending" />
          ) : (
            <FormattedMessage id="auth.signUp.form.submit.label" />
          )}
        </Button>
      </form>
    </Form>
  );
};
